import { generatePresignedUrlCloudinary, generatePresignedUrlDigitalOcean } from "../utils/api-service/post-services";

// helpers/imageUrlHelper.js

/**
 * Get the base URL for images based on environment
 */
export const getImageBaseUrl = () => {
    // You can use environment variables or a config file
    const environment = process.env.REACT_APP_ENVIRONMENT || 'staging';

    const baseUrls = {
        staging: 'https://tulio-staging.sgp1.digitaloceanspaces.com/',
        production: 'https://tulio-production.sgp1.digitaloceanspaces.com/',
        development: 'https://tulio-staging.sgp1.digitaloceanspaces.com/'
    };

    return baseUrls[environment] || baseUrls.staging;
};

/**
 * Construct full image URL from key/path
 */
export const getFullImageUrl = (key) => {
    if (!key) return null;

    // If it's already a full URL, return as is
    if (key.startsWith('http://') || key.startsWith('https://')) {
        return key;
    }

    // Remove leading slash if present to avoid double slashes
    const cleanKey = key.startsWith('/') ? key.substring(1) : key;
    return `${getImageBaseUrl()}${cleanKey}`;
};

/**
 * Extract key from full URL
 */
export const getImageKeyFromUrl = (fullUrl) => {
    if (!fullUrl) return null;

    // If it's not a URL, return as is (might already be a key)
    if (!fullUrl.startsWith('http://') && !fullUrl.startsWith('https://')) {
        return fullUrl;
    }

    const baseUrl = getImageBaseUrl();
    if (fullUrl.startsWith(baseUrl)) {
        return fullUrl.substring(baseUrl.length);
    }

    // If it's from a different base URL, extract the path
    try {
        const url = new URL(fullUrl);
        return url.pathname.substring(1); // Remove leading slash
    } catch (e) {
        return fullUrl;
    }
};

/**
 * Check if a URL is a temporary blob URL
 */
export const isTemporaryUrl = (url) => {
    return url && (url.startsWith('blob:') || url.startsWith('data:'));
};

export async function uploadToCloudinary(file, options) {
    const response = await generatePresignedUrlCloudinary({
        name: file.name,
        module: options.module,
        type: options.type,
        color: options.color
    });

    const { timestamp, signature, folder, public_id } = response;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("api_key", process.env.REACT_APP_CLOUDINARY_API_KEY);
    formData.append("timestamp", timestamp);
    formData.append("signature", signature);
    formData.append("folder", folder);
    formData.append("public_id", public_id);

    const res = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
            method: "POST",
            body: formData,
        }
    );

    return res.json();
}

export async function uploadToDigitalOcean(file, options) {

    const response = await generatePresignedUrlDigitalOcean({
        name: file.name,
        module: options.module,
        type: options.type,
        color: options.color,
        fileType: file.type
    });


    const {
        url,
        publicUrl,
        key
    } = response;


    const upload = await fetch(url, {
        method: "PUT",
        body: file,
        headers: {
            "Content-Type": file.type,
            "x-amz-acl": "public-read"
        }
    });


    if (!upload.ok) {
        throw new Error("DO upload failed");
    }


    return {
        secure_url: publicUrl,
        url: publicUrl,
        key: key
    };
}

const uploadImageUsingPresignedUrl = async (req, res) => {
    try {
        const { presignedUrl, mimeType } = req.body;
        const image = req.files?.image;

        if (!presignedUrl) {
            return res.status(400).json({
                success: false,
                error: 'presignedUrl is required'
            });
        }

        if (!image) {
            return res.status(400).json({
                success: false,
                error: 'image file is required'
            });
        }

        // Validate image type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(image.mimetype)) {
            return res.status(400).json({
                success: false,
                error: 'Only image files (JPEG, PNG, GIF, WEBP) are allowed'
            });
        }

        // Upload image to S3 using the presigned URL
        const uploadResponse = await fetch(presignedUrl, {
            method: 'PUT',
            body: image.data,
            headers: {
                'Content-Type': mimeType || image.mimetype,
                'x-amz-acl': 'public-read'
            }
        });

        if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
        }

        // Extract S3 URL from presigned URL (remove query parameters)
        const s3Url = presignedUrl.split('?')[0];

        return res.json({
            success: true,
            message: 'Image uploaded successfully',
            url: s3Url,
            fileName: image.name,
            fileSize: image.size,
            mimeType: image.mimetype
        });

    } catch (error) {
        console.error('Upload error:', error);
        return res.status(500).json({
            success: false,
            error: error.message
        });
    }
};